import React, { useRef, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import axios from "axios";
import { clientBrandingApi } from "../../../../shared/api";

interface LogoUploadFieldProps {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  /** Disable upload when no client is selected (nothing to scope). */
  disabled?: boolean;
}

/**
 * Logo field with both a URL input and a file upload. Upload flow: presign PUT
 * → upload the file directly to S3 → set the returned public file_url as the
 * field value (the parent saves it with the rest of branding). Shows a small
 * preview of the current URL.
 */
export const LogoUploadField: React.FC<LogoUploadFieldProps> = ({
  label,
  value,
  placeholder,
  onChange,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const { upload_url, file_url } = await clientBrandingApi.createLogoUploadUrl(
        { filename: file.name, content_type: file.type },
      );
      await axios.put(upload_url, file, {
        headers: { "Content-Type": file.type },
      });
      onChange(file_url);
    } catch {
      setError("Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          fullWidth
          size="small"
          label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = "";
          }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={
            uploading ? <CircularProgress size={14} /> : <UploadIcon fontSize="small" />
          }
          onClick={() => inputRef.current?.click()}
          disabled={uploading || disabled}
          sx={{ whiteSpace: "nowrap", mt: 0.25 }}
        >
          Upload
        </Button>
      </Stack>
      {value && (
        <Box
          component="img"
          src={value}
          alt=""
          sx={{
            height: 40,
            maxWidth: 180,
            objectFit: "contain",
            bgcolor: "#0B0F14",
            borderRadius: 1,
            p: 0.5,
          }}
        />
      )}
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Stack>
  );
};
